import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { supabase } from './config/supabase';
import { useFirstAccess } from './hooks/useFirstAccess';
import LoginForm from './components/LoginForm';
import ResetSenhaPrimeiroAcesso from './components/ResetSenhaPrimeiroAcesso';
import Header from './components/Header';
import Footer from './components/Footer';
import EmpresaSelector from './components/EmpresaSelector';
import EmpresaSelectorModal from './components/EmpresaSelectorModal';
import DashboardTab from './pages/DashboardTab';
import CronogramaTab from './pages/CronogramaTab';
import VotacaoTab from './pages/VotacaoTab';
import RelatoriosTab from './pages/RelatoriosTab';
import PlanoTrabalhoTab from './pages/PlanoTrabalhoTab';
import AtaDigitalTab from './pages/AtaDigitalTab';
import DocumentosTab from './pages/DocumentosTab';
import AjudaTab from './pages/AjudaTab';
import AdminPanel from './pages/AdminPanel';
import EmpresasManager from './components/EmpresasManager';
import VotacaoPublica from './pages/VotacaoPublica';
import SemEmpresaMessage from './components/SemEmpresaMessage';
import CadastroPage from './pages/CadastroPage';
import CadastroCandidatoPublico from './pages/CadastroCandidatoPublico';
import CheckoutPage from './pages/CheckoutPage';
import TesteAsaas from './components/checkout/TesteAsaas';
import PagamentoSucesso from './pages/PagamentoSucesso';
import PagamentoErro from './pages/PagamentoErro';
import PagamentoPendente from './pages/PagamentoPendente';
import PagamentoRetorno from './pages/PagamentoRetorno';

// Componente para rotas autenticadas
const AuthenticatedApp: React.FC = () => {
  const { user, loading, empresaSelecionada, setEmpresaSelecionada } = useAuth();
  const { 
    loading: loadingFirstAccess, 
    isPrimeiroAcesso, 
    dadosUsuario, 
    erro: erroFirstAccess,
    isCipaFacilUser,
    concluirPrimeiroAcesso 
  } = useFirstAccess();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [temEmpresas, setTemEmpresas] = useState<boolean | null>(null);
  const [showEmpresaSelectorModal, setShowEmpresaSelectorModal] = useState(false);
  const [empresasUsuario, setEmpresasUsuario] = useState<any[]>([]);

  // Função para verificar se o usuário tem empresas
  const verificarEmpresas = async () => {
    if (!user) {
      return;
    }
    
    try {
      let temEmpresasResult = false;
      let empresasList: any[] = [];
      
      if (user.tipo_usuario === 'SUPER_ADMIN') {
        // Super admin tem acesso a todas as empresas
        const { data } = await supabase
          .from('cp_empresas')
          .select('id, nome, cnpj, razao_social, numero_funcionarios, grau_risco, status, cnae_principal, tipo_comissao')
          .eq('status', 'ATIVA');
        temEmpresasResult = !!(data && data.length > 0);
        empresasList = data || [];
      } else if (user.tipo_usuario === 'ENTERPRISE') {
        // Enterprise tem suas próprias empresas
        const { data } = await supabase
          .from('cp_empresas')
          .select('id, nome, cnpj, razao_social, numero_funcionarios, grau_risco, status, cnae_principal, tipo_comissao')
          .eq('status', 'ATIVA')
          .eq('proprietario_id', user.id);
        temEmpresasResult = !!(data && data.length > 0);
        empresasList = data || [];
      } else {
        // Outros usuários têm acesso através de relacionamentos
        const { data: relacionamentosData } = await supabase
          .from('cp_usuario_empresas')
          .select(`
            empresa:cp_empresas(
              id,
              nome,
              cnpj,
              razao_social,
              numero_funcionarios,
              grau_risco,
              status,
              cnae_principal,
              tipo_comissao
            )
          `)
          .eq('usuario_id', user.id)
          .eq('ativo', true)
          .eq('empresa.status', 'ATIVA');
        
        temEmpresasResult = !!(relacionamentosData && relacionamentosData.length > 0);
        empresasList = relacionamentosData?.map(item => item.empresa).filter(Boolean) || [];
      }
      
      setTemEmpresas(temEmpresasResult);
      setEmpresasUsuario(empresasList);
    } catch (error) {
      console.error('❌ Erro ao verificar empresas:', error);
      setTemEmpresas(false);
      setEmpresasUsuario([]);
    }
  };

  // Limpar estados quando usuário mudar
  React.useEffect(() => {
    if (!user) {
      // Limpar todos os estados relacionados a empresas quando não há usuário
      setTemEmpresas(null);
      setEmpresasUsuario([]);
      setShowEmpresaSelectorModal(false);
      setActiveTab('dashboard');
    }
  }, [user]);

  // Verificar empresas quando o usuário mudar
  React.useEffect(() => {
    if (user && !loading && !loadingFirstAccess) {
      verificarEmpresas();
    }
  }, [user, loading, loadingFirstAccess]);

  // Verificar se precisa mostrar modal de seleção de empresa
  React.useEffect(() => {

    if (user && empresasUsuario.length > 0 && !empresaSelecionada) {
      if (empresasUsuario.length > 1) {
        setShowEmpresaSelectorModal(true);
      } else if (empresasUsuario.length === 1) {
        // Se tiver apenas uma empresa, selecionar automaticamente
        const empresa = empresasUsuario[0];
        const empresaMapeada = {
          id: empresa.id,
          nome: empresa.nome,
          cnpj: empresa.cnpj,
          tipo_acesso: empresa.tipo_acesso || 'GESTOR',
          ativo: empresa.ativo ?? true,
          data_termino_mandato: empresa.data_termino_mandato,
          tipo_comissao: (empresa as any).tipo_comissao,
          cnae_principal: (empresa as any).cnae_principal,
          status: (empresa as any).status
        };
        setEmpresaSelecionada(empresaMapeada);
      }
    }
  }, [user, empresasUsuario, empresaSelecionada]);

  // Loading geral (auth + primeiro acesso)
  if (loading || loadingFirstAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // Se não estiver autenticado, mostrar página de login
  if (!user) {
    return <LoginForm />;
  }

  // Se houve erro na verificação do primeiro acesso
  if (erroFirstAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          <h2 className="text-xl font-bold text-red-800 mb-2">Erro de Acesso</h2>
          <p className="text-red-600 mb-4">{erroFirstAccess}</p>
          <p className="text-sm text-gray-600">
            Entre em contato com o administrador do sistema.
          </p>
        </div>
      </div>
    );
  }

  // Se não é usuário do CIPA Fácil
  if (!isCipaFacilUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          <h2 className="text-xl font-bold text-yellow-800 mb-2">Acesso Não Autorizado</h2>
          <p className="text-yellow-700 mb-4">
            Seu usuário não tem permissão para acessar o CIPA Fácil.
          </p>
          <p className="text-sm text-gray-600">
            Entre em contato com o administrador para solicitar acesso.
          </p>
        </div>
      </div>
    );
  }

  // Se é primeiro acesso, mostrar tela de reset de senha
  if (isPrimeiroAcesso && dadosUsuario) {
    return (
      <ResetSenhaPrimeiroAcesso
        usuario={{
          id: dadosUsuario.id,
          email: dadosUsuario.email,
          nome_completo: dadosUsuario.nome_completo
        }}
        onSuccess={async () => {
          await concluirPrimeiroAcesso();
          // O useFirstAccess vai atualizar automaticamente o estado
        }}
      />
    );
  }

  const renderTabContent = () => {
    // Se não tem empresas e não está na aba de empresas ou admin, mostrar mensagem
    if (temEmpresas === false && !['empresas', 'admin'].includes(activeTab)) {
      return (
        <SemEmpresaMessage 
          onCadastrarEmpresa={() => setActiveTab('empresas')}
        />
      );
    }

    switch (activeTab) {
      case 'dashboard':
        return <DashboardTab />;
      case 'cronograma':
        return <CronogramaTab />;
      case 'votacao':
        return <VotacaoTab />;
      case 'relatorios':
        return <RelatoriosTab />;
      case 'plano-trabalho':
        if (user?.tipo_usuario !== 'ENTERPRISE') {
          return <DashboardTab />;
        }
        return <PlanoTrabalhoTab />;
      case 'ata-digital':
        if (user?.tipo_usuario !== 'ENTERPRISE') {
          return <DashboardTab />;
        }
        return <AtaDigitalTab />;
      case 'documentos':
        return <DocumentosTab />;
      case 'ajuda':
        return <AjudaTab />;
      case 'empresas':
        return user ? (
          <EmpresasManager 
            usuarioId={user.id} 
            tipoUsuario={user.tipo_usuario}
            onEmpresaChange={() => verificarEmpresas()}
          />
        ) : null;
      case 'admin':
        return <AdminPanel />;
      default:
        return <DashboardTab />;
    }
  };

  // Determinar cor de fundo baseada no tipo de comissão
  const getBackgroundClass = () => {
    const tipoComissao = empresaSelecionada?.tipo_comissao;
    
    if (tipoComissao === 'CIPAMIN') {
      return 'bg-orange-50'; // Fundo laranja claro para mineração
    } else if (tipoComissao === 'CIPATR') {
      return 'bg-green-50'; // Fundo verde claro para rural
    }
    
    return 'bg-gray-50'; // Fundo padrão para CIPA
  };

  return (
    <div className={`min-h-screen ${getBackgroundClass()} flex flex-col transition-colors duration-300`}>
      <Header activeTab={activeTab} setActiveTab={setActiveTab} temEmpresas={temEmpresas ?? true} />
      
      {/* Seletor de Empresa - apenas se não for super admin */}
      {user?.tipo_usuario !== 'SUPER_ADMIN' && (
        <div className="bg-white border-b px-4 py-3">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Empresa:</span>
              <EmpresaSelector 
                empresaSelecionada={empresaSelecionada}
                onEmpresaChange={setEmpresaSelecionada}
              />
            </div>
            {empresaSelecionada && (
              <div className="text-sm text-gray-600">
                {empresaSelecionada.nome} - {empresaSelecionada.cnpj}
              </div>
            )}
          </div>
        </div>
      )}
      
      <main className="container mx-auto px-4 py-6 flex-1">
        {renderTabContent()}
      </main>
      <Footer />
      
      {/* Modal de Seleção de Empresa */}
      {user && (
        <EmpresaSelectorModal
          isOpen={showEmpresaSelectorModal}
          onClose={() => setShowEmpresaSelectorModal(false)}
          onSelectEmpresa={(empresa) => {
            // Mapear empresa para o formato esperado pelo contexto
            const empresaMapeada = {
              id: empresa.id,
              nome: empresa.nome,
              cnpj: empresa.cnpj,
              tipo_acesso: empresa.tipo_acesso || 'GESTOR',
              ativo: empresa.ativo ?? true,
              data_termino_mandato: empresa.data_termino_mandato,
              tipo_comissao: (empresa as any).tipo_comissao,
              cnae_principal: (empresa as any).cnae_principal,
              status: (empresa as any).status
            };
            setEmpresaSelecionada(empresaMapeada);
            setShowEmpresaSelectorModal(false);
          }}
          usuarioId={user.id}
        />
      )}
    </div>
  );
};

// Componente principal do App
const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Rota pública para votação */}
        <Route path="/:empresaSlug" element={<VotacaoPublica />} />
        
        {/* Rota pública para cadastro de candidatos */}
        <Route path="/candidato/:slug" element={<CadastroCandidatoPublico />} />
        
        {/* Rota de cadastro - sem autenticação */}
        <Route path="/cadastro" element={<CadastroPage />} />
        
        {/* Rota de checkout - sem autenticação */}
        <Route path="/checkout" element={<CheckoutPage />} />
        
        {/* Rota de teste Asaas - sem autenticação */}
        <Route path="/teste-asaas" element={<TesteAsaas />} />
        
               {/* Rotas de pagamento - sem autenticação */}
               <Route path="/pagamento/retorno" element={<PagamentoRetorno />} />
               <Route path="/pagamento/sucesso" element={<PagamentoSucesso />} />
               <Route path="/pagamento/erro" element={<PagamentoErro />} />
               <Route path="/pagamento/pendente" element={<PagamentoPendente />} />
        
        {/* Rota principal do sistema - com autenticação */}
        <Route path="/" element={<AuthenticatedApp />} />
        
        {/* Redirecionar qualquer outra rota para a página principal */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

export default App;
